"use client";
import { useEffect, useRef } from "react";
import { useUIStore } from "@/stores/ui-store";

interface DelegationCompleted {
  type: "delegation:completed";
  delegation_id: string;
  worker_id: string;
  worker_name: string;
  response: string;
  provider: string;
}

interface DelegationFailed {
  type: "delegation:failed";
  delegation_id: string;
  error: string;
}

export function useDelegationStream() {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Capture latest callbacks via refs so the effect dep array stays empty
  const addNotificationRef = useRef(useUIStore.getState().addNotification);
  const addChatMessageRef = useRef(useUIStore.getState().addChatMessage);

  useEffect(() => {
    // Keep refs fresh when the store updates (stable Zustand actions, but be defensive)
    return useUIStore.subscribe((state) => {
      addNotificationRef.current = state.addNotification;
      addChatMessageRef.current = state.addChatMessage;
    });
  }, []);

  useEffect(() => {
    let destroyed = false;

    function connect() {
      if (destroyed) return;
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${protocol}//${window.location.host}`);
      wsRef.current = ws;

      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data as string) as { type: string };
          if (msg.type === "delegation:completed") {
            const m = msg as DelegationCompleted;
            addNotificationRef.current({ message: `${m.worker_name} completed task`, type: "success" });
            addChatMessageRef.current(
              { role: "assistant", content: m.response, surggieId: m.worker_id },
              m.worker_id
            );
          } else if (msg.type === "delegation:failed") {
            const m = msg as DelegationFailed;
            addNotificationRef.current({
              message: `Delegation failed: ${m.error.slice(0, 80)}`,
              type: "info",
            });
          }
        } catch {
          // ignore malformed frames
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (!destroyed) {
          reconnectRef.current = setTimeout(connect, 1000);
        }
      };
    }

    connect();

    return () => {
      destroyed = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, []); // empty — one WS for the lifetime of the component
}
